import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { createGlobalStyle } from 'styled-components';

import CookieInitializer from 'app/lib/CookieInitializer';
import { isReviewApp } from 'app/lib/environmentChecker';
import ThirdPartyScriptLoader from 'app/lib/ThirdPartyScriptLoader';
import { commonTheme } from 'app/shared/theme';
import { CookieBotContext } from 'app/shared/context/CookieBot';

const CookieBotGlobalStyle = createGlobalStyle`
  #CookiebotWidget #CookiebotWidget-buttons button,
  #CybotCookiebotDialogFooter .CybotCookiebotDialogBodyButton {
    font-family: ${commonTheme.fonts.regular};
    font-weight: ${commonTheme.fontWeights.bold};
    letter-spacing:  ${commonTheme.letterSpacings.button};
    line-height: 100% !important;
    text-transform: uppercase;
    text-decoration: none;
    padding: ${commonTheme.ruler[3]}px ${commonTheme.ruler[6]}px !important;
    border-radius: 60px !important;
    border-width: 1px !important;
    outline: inherit;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${commonTheme.ruler[2]}px;

    min-width: max-content;
    height: ${commonTheme.dimensions.button.height} !important;
    min-height: unset !important;

    ${commonTheme.media.mobile`
      font-size: ${commonTheme.fontSizes.fontSize100.mobile};
    `};

    ${commonTheme.media.smallDesktop`
      font-size: ${commonTheme.fontSizes.fontSize100.desktop};
    `};
  }

  #CybotCookiebotDialogFooter {
    display: flex !important;
  }

  #CybotCookiebotDialogFooter #CybotCookiebotDialogBodyLevelButtonCustomize .CybotCookiebotDialogArrow {
    margin-left: 0 !important;
  }
`;

const primaryDomainGroupId = '455d680e-7d29-4c0d-9bbb-b451e4bdec11';
const otherDomainGroupIds = [
  {
    region:
      'AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE,GB', // EU and UK
    cbid: 'ebd109ff-0f57-49a4-b7a4-3d4d17169739',
  },
  {
    region: 'BR', // Brazil
    cbid: '8bd740c7-1217-41ad-ab19-fd294aec290a',
  },
  {
    region: 'ZA', // South Africa
    cbid: '5045a945-994f-4081-9276-a997f5e2b932',
  },
  {
    region: 'US-06', // California
    cbid: '73e8a433-ad1a-4d58-a561-bcdcc6b55d5c',
  },
  {
    region: 'US-08', // Colorado
    cbid: '7093d070-db16-4d13-b4a5-5aebc45044e2',
  },
  {
    region: 'US-09', // Connecticut
    cbid: '31b53f72-68e3-46ee-9a99-b75010b86bfc',
  },
  {
    region: 'US-49', // Utah
    cbid: 'b0fa97c6-9b85-439e-baa0-d3a827f386db',
  },
  {
    region: 'US-51', // Virginia
    cbid: '5e69bb8f-08f5-445b-b604-3a0520987588',
  },
];

function withCookieBot(
  PassedComponent: React.ComponentType<React.PropsWithChildren<any>>,
  canSuppressCookieBot?: boolean
) {
  const WrappedComponent = (props: any) => {
    const WrappedComponentWithoutCookieBot = (props: any) => (
      <CookieInitializer>
        <ThirdPartyScriptLoader>
          <PassedComponent {...props} />
        </ThirdPartyScriptLoader>
      </CookieInitializer>
    );

    const WrappedComponentWithCookieBot = (props: any) => {
      window.Cookiebot = window.Cookiebot || {};
      const cookieBotContextValue = useContext(CookieBotContext);
      const [customCookieBotStyle, setCustomCookieBotStyle] =
        useState<string>('');

      const customStyleToHideCookieBotIcon = `
          #CookiebotWidget {
            display: none !important;
          }
      `;

      // For future: if we ever decide we want to just MOVE THE COOKIEBOT ICON TO A DIFFERENT PLACE
      // on the screen instead of hiding it altogether, the following css code was tested and should work:
      //    #CookiebotWidget,
      //    div[id^="CookiebotWidget"] {
      //      bottom: 100px !important; /* Adjust height */
      //      left: 20px !important;   /* Adjust horizontal alignment */
      //    }
      //
      // Alternatively, if we ever want to change the corner the CookieBot icon appears in on a particular
      // page, the following options passed to the script tag below with id="Cookiebot" work:
      //
      //   data-widget-enabled="false" (or true)
      //   data-widget-position="bottom-left" (or top-right, etc. - 4 possible options)
      //
      // Just note that setting these dynamically after the page has already been loaded causes rendering
      // issues related to hoc's and such - but they could be helpful for hiding or moving the CookieBot
      // icon on initial page load, for the entire duration of the page life

      useEffect(() => {
        if (canSuppressCookieBot) {
          if (cookieBotContextValue.suppressCookieBot) {
            setCustomCookieBotStyle(customStyleToHideCookieBotIcon);
          } else {
            setCustomCookieBotStyle('');
          }
        }
      }, [cookieBotContextValue.suppressCookieBot]);

      const dataGeoregionsJson = JSON.stringify(otherDomainGroupIds);
      const dataGeoregions = dataGeoregionsJson
        .replace('[', '')
        .replace(']', '')
        .replace(/"/g, "'");

      return (
        <>
          <CookieBotGlobalStyle />
          <Helmet>
            <script
              id="Cookiebot"
              src="https://consent.cookiebot.com/uc.js"
              data-cbid={primaryDomainGroupId}
              data-georegions={dataGeoregions}
              data-blockingmode="auto"
              type="text/javascript"
            />
            <script
              id="CookieDeclaration"
              src={`https://consent.cookiebot.com/${primaryDomainGroupId}/cd.js`}
              data-georegions={dataGeoregions}
              type="text/javascript"
              async
            />
            <script
              src="https://js.stripe.com/basil/stripe.js"
              async
              data-cookieconsent="ignore"
            />
            <style type="text/css">{customCookieBotStyle}</style>
          </Helmet>
          <WrappedComponentWithoutCookieBot {...props} />
        </>
      );
    };

    const shouldRenderCookieBot =
      process.env.NODE_ENV === 'production' && !isReviewApp();

    if (shouldRenderCookieBot) {
      return <WrappedComponentWithCookieBot {...props} />;
    }

    return <WrappedComponentWithoutCookieBot {...props} />;
  };

  return WrappedComponent;
}

export default withCookieBot;
